// ── LeadClaudePanel (Evo Brain — panel 3) ────────────────────────────────────
// Ya no renderiza UI propia — el shell genérico vive en AssistantDock (ver
// contexts/AssistantContext.tsx). Esto solo registra la entidad activa y
// conserva lo propio de leads: los chips contextuales (quick actions) y el
// link de la entidad para la ApprovalCard.

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '../ui/Button.js';
import { useAssistantModule, useRegisterAssistantActive } from '../../contexts/AssistantContext.js';
import {
  useLeadDrafts,
  type LeadRow,
  type LeadAnalysis,
  type OutreachDraft,
} from '../../hooks/useLeads.js';

// ── Quick-action chips — mismos prompts de siempre, enviados via assistant.sendMessage.
interface Chip {
  i18nKey: string;
  i18nVars?: Record<string, string>;
  prompt: string;
}

function getContextualChips(
  analysis: LeadAnalysis | null | undefined,
  drafts: OutreachDraft[] | undefined,
  leadLanguage: string,
): Chip[] {
  const chips: Chip[] = [];

  if (analysis?.analysisStatus === 'completed') {
    // 'Refinar resumen' se quitó: duplicaba la pill fijada 'Regenerar resumen'
    // (misma tool regenerate_summary) — dos botones para lo mismo en el panel.
    chips.push({
      i18nKey: 'claude.quick.explain_score',
      prompt: `Why is this lead priority ${analysis.priorityScore ?? '?'}/10?`,
    });
    chips.push({
      i18nKey: 'claude.quick.more_opportunities',
      prompt: 'Suggest 2-3 additional opportunities I might have missed for this business.',
    });
  }

  const pendingDrafts = drafts?.filter(d => d.status === 'pending_review') ?? [];
  if (pendingDrafts.length > 0) {
    // 'Traducir a…' se quitó: duplicaba la pill fijada 'Traducir borrador'
    // (misma tool translate_draft).
    chips.push({
      i18nKey: 'claude.quick.make_shorter',
      prompt: 'Make the initial draft shorter and more direct, max 80 words. Use the update_draft tool.',
    });
    chips.push({
      i18nKey: 'claude.quick.more_direct',
      prompt: 'Rewrite the initial draft with a more direct, confident tone. Use the update_draft tool.',
    });
  }

  return chips;
}

// ── Props ─────────────────────────────────────────────────────────────────────

interface Props {
  lead: LeadRow;
  analysis?: LeadAnalysis | null;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function LeadClaudePanel({ lead, analysis }: Props) {
  const { t } = useTranslation();
  const { data: drafts } = useLeadDrafts(lead.id);

  const assistant = useAssistantModule('lead', lead.id, undefined);

  const chips = useMemo(
    () => getContextualChips(analysis, drafts, lead.language ?? 'en'),
    [analysis, drafts, lead.language],
  );

  const visibleMessagesEmpty = assistant.messages.filter(m => m.role !== 'tool_call').length === 0;

  const topContent = chips.length > 0 && visibleMessagesEmpty ? (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--evo-space-2)', padding: 'var(--evo-space-2) var(--evo-space-4)', borderBottom: '1px solid var(--evo-stroke)' }}>
      {chips.map(chip => (
        <Button
          key={chip.i18nKey + (chip.i18nVars?.['lang'] ?? '')}
          variant="outline"
          tone="cyan"
          size="xs"
          align="left"
          py="7px"
          disabled={assistant.streaming.phase !== 'idle'}
          onClick={() => { void assistant.sendMessage(chip.prompt); }}
        >
          {chip.i18nVars
            ? t(chip.i18nKey as Parameters<typeof t>[0], chip.i18nVars)
            : t(chip.i18nKey as Parameters<typeof t>[0])}
        </Button>
      ))}
    </div>
  ) : undefined;

  useRegisterAssistantActive('lead', lead.id, {
    title: t('leads.assistant.title'),
    placeholder: t('leads.assistant.placeholder'),
    emptyHint: t('leads.assistant.hint'),
    entityLink: { label: lead.businessName, href: `/leads/${lead.id}` },
    topContent,
  });

  return null;
}
